/* ============================================================
   melusio-cart.js — Cart AJAX engine for ALL cart surfaces (Cut 4).

   A "surface" is any [data-cart-surface] with a [data-cart-content] region and a
   data-section-id: the bag drawer (melusio-cart) and, on /cart, the cart page
   (melusio-cart-page). One engine drives both — every mutation goes through the Cart
   AJAX API with BUNDLED SECTION RENDERING (all present surface ids at once) and each
   surface's [data-cart-content] is replaced verbatim from response.sections[<id>].

   Server is the source of truth: nothing here computes money/quantities/availability.
   Section ids and route URLs are read from the DOM, never hardcoded; a null section
   falls back to a badge reconcile. Exposes window.MelusioCart.addItem / openDrawer for
   the shared buy box (melusio-buy-box.js); reuses window.MelusioCart.reconcile (header).
   ============================================================ */
(function () {
  // ONE ENGINE PER PAGE. The bag drawer loads this file on every page and /cart used to
  // load it again, which wired every control twice: one remove click sent two requests
  // and the second failed (#231). A second copy, from anywhere, now does nothing.
  if (window.MelusioCart && window.MelusioCart.engine) return;

  // Copy is passed in from Liquid (melusio-cart.liquid renders window.MelusioCartStrings
  // in the always-present bag drawer) from the "Text: bag" theme settings.
  const M = window.MelusioCartStrings || {};
  const surfaces = () => Array.from(document.querySelectorAll('[data-cart-surface]'));
  if (!surfaces().length) return;

  function urls() {
    const s = document.querySelector('[data-cart-add-url]') || {};
    const get = (a, d) => (s.getAttribute ? s.getAttribute(a) : null) || d;
    return {
      add: get('data-cart-add-url', '/cart/add') + '.js',
      change: get('data-cart-change-url', '/cart/change') + '.js',
      clear: get('data-cart-url', '/cart') + '/clear.js',
    };
  }
  const sectionParam = () => surfaces().map((s) => s.getAttribute('data-section-id')).filter(Boolean).join(',');

  let busy = false;
  function setBusy(on, li) {
    busy = on;
    document.querySelectorAll('[data-cart-surface] [data-cart-inc], [data-cart-surface] [data-cart-dec], [data-cart-surface] [data-cart-remove]').forEach((b) => { if (b.tagName === 'BUTTON') b.disabled = on; });
    if (li && li.isConnected) li.setAttribute('aria-busy', on ? 'true' : 'false');
  }
  function announce(msg) { document.querySelectorAll('[data-cart-live]').forEach((el) => { el.textContent = msg; }); }
  function showLineError(li, msg) {
    if (!li) return;
    const el = li.querySelector('[data-line-error]');
    if (el) { el.textContent = msg; el.hidden = false; }
    announce(msg);
  }
  function clearLineError(li) {
    const el = li && li.querySelector('[data-line-error]');
    if (el) { el.textContent = ''; el.hidden = true; }
  }

  // ---- swap a surface's [data-cart-content] from bundled-section HTML, preserving
  //      the items scroll position and syncing the head count ----
  function replaceSurface(surface, html) {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const fresh = doc.querySelector('[data-cart-content]');
    const live = surface.querySelector('[data-cart-content]');
    if (!fresh || !live) return false;
    const liveItems = live.querySelector('[data-cart-items]');
    const scrollTop = liveItems ? liveItems.scrollTop : 0;
    live.replaceWith(fresh);
    const newItems = fresh.querySelector('[data-cart-items]');
    if (newItems) newItems.scrollTop = scrollTop;
    const freshTitle = doc.querySelector('.m-cart__count');
    const liveTitleWrap = surface.querySelector('.m-cart__count');
    // sync count text if both present (head lives outside [data-cart-content])
    if (freshTitle && liveTitleWrap) liveTitleWrap.textContent = freshTitle.textContent;
    else if (liveTitleWrap && !freshTitle) liveTitleWrap.remove();
    return true;
  }

  function badge(data) {
    const mc = window.MelusioCart || {};
    if (typeof data.item_count === 'number' && mc.setCount) mc.setCount(data.item_count);
    else if (mc.reconcile) mc.reconcile();
  }

  function applyResponse(data, message) {
    const secs = data && data.sections;
    if (secs) {
      surfaces().forEach((surface) => {
        const id = surface.getAttribute('data-section-id');
        const html = id ? secs[id] : null;
        if (html) replaceSurface(surface, html);
      });
    } else {
      console.warn('[melusio-cart] no rendered sections in response — refreshing badge only');
    }
    badge(data);
    if (message) announce(message);
  }

  async function post(url, payload) {
    const res = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(Object.assign({ sections: sectionParam() }, payload)),
    });
    let data = {};
    try { data = await res.json(); } catch (_) {}
    return { ok: res.ok, data };
  }

  // What a shopper reads when Shopify refuses a change. A stock limit (422) comes with a
  // sentence written for shoppers ("You can only add 3 ..."), so it is shown as it is;
  // anything else is technical ("no valid id or line parameter") and gets the theme's copy.
  const refusal = (data, fallback) => (data.status === 422 && data.description) || fallback;

  async function changeLine(key, quantity, li) {
    if (busy) return;
    setBusy(true, li);
    clearLineError(li);
    try {
      const { ok, data } = await post(urls().change, { id: key, quantity: quantity });
      if (!ok) { showLineError(li, refusal(data, M.updateFailed)); return; }
      applyResponse(data, quantity === 0 ? M.removed : M.updated);
    } catch (_) {
      showLineError(li, M.error);
    } finally {
      setBusy(false, li);
    }
  }

  async function addItem(variantId, quantity) {
    if (busy) return { ok: false, error: M.busy };
    setBusy(true);
    try {
      const { ok, data } = await post(urls().add, { items: [{ id: variantId, quantity: quantity || 1 }] });
      if (!ok) return { ok: false, error: refusal(data, M.addFailed) };
      applyResponse(data, M.added);
      openDrawer();
      return { ok: true };
    } catch (_) {
      return { ok: false, error: M.error };
    } finally {
      setBusy(false);
    }
  }

  function openDrawer(trigger) {
    if (window.MelusioOverlay && window.MelusioOverlay.open) window.MelusioOverlay.open('melusio-cart', trigger);
  }

  function readQty(li) {
    const el = li.querySelector('[data-cart-qty]');
    if (!el) return 0;
    const raw = el.tagName === 'INPUT' ? el.value : el.textContent;
    return parseInt(raw, 10) || 0;
  }

  // ---- delegated stepper / remove across every surface ----
  document.addEventListener('click', (e) => {
    if (!e.target.closest('[data-cart-surface]')) return;
    const control = e.target.closest('[data-cart-inc],[data-cart-dec],[data-cart-remove]');
    if (!control || (control.tagName === 'BUTTON' && control.disabled)) return;
    const li = control.closest('[data-cart-line]');
    if (!li) return;
    e.preventDefault(); // intercept the page's native remove <a> / submit when JS is on
    const key = li.getAttribute('data-line-key');
    const current = readQty(li);
    let next;
    if (control.hasAttribute('data-cart-remove')) next = 0;
    else if (control.hasAttribute('data-cart-inc')) next = current + 1;
    else next = current - 1; // decrement below 1 removes
    changeLine(key, Math.max(0, next), li);
  });

  // ---- cart page: commit a typed quantity (change fires on blur / Enter) ----
  document.addEventListener('change', (e) => {
    const input = e.target.closest('input[data-cart-qty]');
    if (!input) return;
    const li = input.closest('[data-cart-line]');
    if (!li) return;
    let q = parseInt(input.value, 10);
    if (isNaN(q) || q < 0) q = 0;
    changeLine(li.getAttribute('data-line-key'), q, li);
  });

  window.MelusioCart = Object.assign(window.MelusioCart || {}, { addItem, openDrawer, engine: true });
})();
